import { z } from 'zod';
import { baseComponentSchema, imageCropSchema, buttonSchema } from '../component-base.schemas';

export const featureSchema = z.object({
  id: z.string().uuid('Invalid feature ID').optional(),
  title: z.string().min(1, 'Feature title is required'),
  description: z.string().min(1, 'Feature description is required'),
  icon: z.string().optional(),
  imageUrl: z.string().url('Invalid image URL').optional(),
  imageAlt: z.string().max(200, 'Image alt text must be less than 200 characters').optional(),

  // Image upload metadata
  mediaId: z.string().uuid().optional(),
  s3Key: z.string().optional(),
  cloudfrontUrl: z.string().url().optional(),

  // Image crop data
  crop: imageCropSchema,

  order: z.number().int().min(0).optional(),
});

// Features custom colors schema - allows overriding theme colors per component instance
export const featuresCustomColorsSchema = z
  .object({
    background: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
    text: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
    title: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
    cardBackground: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
    cardBorder: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
    iconColor: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
    buttonBg: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
    buttonText: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
  })
  .optional();

export const featuresDataSchema = z.object({
  title: z.string().min(1, 'Features section title is required'),
  subtitle: z.string().max(300, 'Subtitle must be less than 300 characters').optional(),
  features: z.array(featureSchema),
  layout: z.enum(['grid', 'list', 'carousel']).default('grid'),
  columns: z.enum(['1', '2', '3', '4']).default('3'),
  displayMode: z.enum(['icon', 'image', 'both']).default('icon').optional(),
  imageShape: z.enum(['square', 'circle', 'rounded']).default('rounded').optional(),
  imageSize: z.enum(['small', 'medium', 'large']).default('medium').optional(),
  showIcons: z.boolean().default(true).optional(),
  // DEPRECATED: Use customColors.background instead (kept for backward compatibility)
  backgroundColor: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/, 'Invalid color format')
    .optional(),
  // DEPRECATED: Use customColors.text instead (kept for backward compatibility)
  textColor: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/, 'Invalid color format')
    .optional(),
  // NEW: Component-specific custom colors that override theme colors
  customColors: featuresCustomColorsSchema,
  // Optional call-to-action buttons
  buttons: z.array(buttonSchema).optional(),
});

export const featuresComponentSchema = baseComponentSchema.extend({
  type: z.literal('features'),
  data: featuresDataSchema,
});

// Type exports
export type Feature = z.infer<typeof featureSchema>;
export type FeaturesData = z.infer<typeof featuresDataSchema>;
export type FeaturesComponent = z.infer<typeof featuresComponentSchema>;
